import React, { useEffect, useState } from 'react';
import InfiniteLoader from '../../../components/multiSelector/infinityLoader';
import { Modal, ModalBody, UncontrolledTooltip } from 'reactstrap';
import CustomButton from '../../../components/customButton';
import SiteGroupSelector from '../../../components/filterSelector/SiteGroupSelector';
import { renderSolution } from '../../../utils/tables';
import API from '../../../services/api';
import utils from '../../../utils';
import { setAlert } from '../../../redux/dashboard/actions';
import { useDispatch, useSelector } from 'react-redux';
import IconButton from '../../../components/iconButton';
import { getUser } from '../../../redux/user/actions';

const Sites = ({ siteGroup, createMode, onRefresh }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [sites, setSites] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [selectedSiteGroup, setSelectedSiteGroup] = useState(null);
  const [currentFilter, setCurrentFilter] = useState('');
  const [totalSites, setTotalSites] = useState(0);
  const [company, setCompany] = useState(null);

  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const canTransfer = !createMode && !!user?.id && (
    user.admin || (company?.id === siteGroup.companyId && company?.ownerId === user.id)
  );

  useEffect(() => {
    let current = true;
    setCompany(null);
    setModalOpen(false);
    setSelectedSites([]);
    setSelectedSiteGroup(null);
    if (!createMode && !user?.admin && siteGroup.companyId) {
      API.getCompany(siteGroup.companyId)
        .then(company => {
          if (current) setCompany(company);
        })
        .catch(() => {});
    }
    return () => { current = false; };
  }, [siteGroup.companyId, user?.id, user?.admin, createMode]);

  useEffect(() => {
    if (siteGroup) {
      setTotalSites(0);
      onLoading();
    }
  }, [siteGroup]);

  const toggle = () => {
    setModalOpen(!modalOpen);
  };

  const transferSites = () => {
    if (!canTransfer) return;
    const siteNames = selectedSites.map(site => site.name);
    const transferObject = {
      names: siteNames,
      targetProjectId: selectedSiteGroup.value.replaceAll('"', '')
    };

    API.transferSites(siteGroup.id, transferObject)
      .then(() => {
        onLoading();
        dispatch(setAlert(utils.generateAlert('Sites were transferred.', 'success')));
        onRefresh(Date.now());
      })
      .catch(err => {
        const message = err?.code === 'FORBIDDEN' || err?.status === 403
          ? 'Only the company owner or an AdMobilize administrator can transfer sites.'
          : 'Sites could not be transferred. Something went wrong!';
        dispatch(setAlert(utils.generateAlert(message, 'error')));
      })
      .finally(() => {
        setSelectedSiteGroup(null);
        setSelectedSites([]);
      });
  };

  const getSize = () => {
    return createMode ? 56 : 40;
  };

  const onLoading = (nextPageToken = '', searchingBy = '') => {
    if (!siteGroup || !siteGroup.id) {
      return;
    }

    const query = encodeURI(`displayName ILIKE "%${searchingBy}%" OR id ILIKE "%${searchingBy}%"`);
    const filter = searchingBy !== '' ? query : '';

    if (filter !== currentFilter) {
      setSites([]);
    }

    return API.getSites(siteGroup.id, nextPageToken, filter).then(res => {
      if (filter === '') {
        setTotalSites(res.totalSize);
      }
      const fetchedSites = res.sites.map(site => ({ ...site, id: site.id.trim() }));

      // Reset list when filter changes
      if (filter !== currentFilter) {
        setCurrentFilter(filter);
        setSites(fetchedSites);
      } else {
        setSites(fetchedSites);
      }

      return res;
    });
  };

  const renderSite = site => {
    const fields = [
      {
        id: 'displayName',
        name: 'Site'
      },
      {
        id: 'siteGroup',
        name: 'Site Group'
      }
    ];

    const newSite = fields.map(field => {
      const key = `${field.id}-${site.id}`;
      return (
        <div key={key} className="cell">
          <div id={key}>{site[field.id]}</div>
          <UncontrolledTooltip placement="left" target={`#${key}`}>
            <div>
              <span className="d-flex">
                <span>{field.name}</span>
              </span>
            </div>
          </UncontrolledTooltip>
        </div>
      );
    });
    newSite.push(renderSolution(site.solution, `solution-icon-${site.id}`));
    return newSite;
  };

  const renderBulkActionButtons = selectedSites => {
    return canTransfer ? <IconButton icon={'transfer'} disabled={selectedSites.length === 0} onPress={toggle} /> : null;
  };

  if (!sites) {
    return null;
  }
  return (
    <>
      {canTransfer && <Modal className="modal-transfer-sites" isOpen={modalOpen} fade={false} toggle={toggle} centered>
        <ModalBody>
          <div className="transfer-sites">
            <h3>{`Transfer ${selectedSites.length > 1 ? 'sites' : 'site'} to another Site Group`}</h3>
            <h4>
              {`Select a site group to transfer ${selectedSites.length} ${
                selectedSites.length > 1 ? ' sites' : ' site'
              }`}
            </h4>
            <SiteGroupSelector
              onSelect={(selectedValue, sitegroups) => {
                setSelectedSiteGroup(sitegroups.options.find(sitegroup => sitegroup.value === selectedValue));
              }}
              selected={selectedSiteGroup}
              customFilter={{ field: 'companyId', value: siteGroup.companyId }}
            />
          </div>
          <div className="button-group">
            <CustomButton
              title={'Cancel'}
              classes={'btn-secondary'}
              handleClick={() => {
                toggle();
                setSelectedSiteGroup(null);
              }}
            />
            <CustomButton
              title={'Confirm'}
              disabled={!selectedSiteGroup}
              loading={false}
              classes={'btn-primary'}
              handleClick={() => {
                toggle();
                transferSites();
              }}
            />
          </div>
        </ModalBody>
      </Modal>}

      <InfiniteLoader
        items={sites}
        allSelected={false}
        displayField="displayName"
        customRender={createMode ? renderSite : null}
        emptyMessage="No Sites found for this site group"
        customSearchPlaceHolder="Search Sites"
        sortField="displayName"
        onSelect={setSelectedSites}
        listSize={sites.length * getSize()}
        itemHeight={getSize()}
        renderCheckbox={true}
        renderBulkActionButtons={renderBulkActionButtons}
        onLoading={onLoading}
        totalItems={totalSites}
        shouldUpdate={siteGroup.id ? true : false}
      />
    </>
  );
};

export default Sites;
